import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches unhandled React errors
 * 
 * Prevents white screen of death by showing user-friendly error UI
 * with options to retry or navigate home.
 * 
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled React error:', error, errorInfo);
    
    // TODO: Send to error tracking service (e.g., Sentry)
    // trackError(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      return (
        <DefaultErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ error, onReset }) => {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <Card className="max-w-md w-full">
        <div className="flex flex-col items-center text-center p-4">
          {/* Error icon */}
          <div className="flex items-center justify-center w-16 h-16 bg-error-50 rounded-full mb-6">
            <AlertTriangle className="h-8 w-8 text-error-500" />
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Something went wrong
          </h1>
          
          {/* Description */}
          <p className="text-sm text-neutral-600 mb-6">
            An unexpected error occurred. Please try reloading the page or return to the home page.
          </p>
          
          {/* Error details in development */}
          {process.env.NODE_ENV === 'development' && (
            <details className="w-full mb-6 text-left">
              <summary className="cursor-pointer text-xs font-medium text-neutral-700 hover:text-neutral-900 mb-2">
                Error Details (Development Only)
              </summary>
              <pre className="text-xs p-3 bg-neutral-100 rounded-md overflow-auto max-h-48 text-neutral-800">
                {error.message}
                {error.stack && (
                  <>
                    {'\n\n'}
                    {error.stack}
                  </>
                )}
              </pre>
            </details>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={handleReload}
              variant="primary"
              size="md"
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4" />
              Reload Page
            </Button>
            <Button
              onClick={handleGoHome}
              variant="ghost"
              size="md"
              className="flex-1"
            >
              <Home className="h-4 w-4" />
              Go to Home
            </Button>
          </div>
          
          {/* Alternative: Reset button (tries to recover without reload) */}
          <button
            onClick={onReset}
            className="mt-4 text-xs text-neutral-500 hover:text-neutral-700 underline"
          >
            Try to recover without reloading
          </button>
        </div>
      </Card>
    </div>
  );
};
